"use strict";

/**
 * @enum {string}
 */
gc.gis.util.GCTreeNodeEvent = {
	/**
	 * @event gc.gis.util.GCTreeNodeEvent#change:payload
	 * @type {Object}
	 * @property {gc.gis.util.GCTreeNode} context
	 * @property {*} oldValue
	 * @property {*} newValue
	 */
	PAYLOAD_CHANGED:  'change:payload',
	/**
	 * @event gc.gis.util.GCTreeNodeEvent#change:parent
	 * @type {Object}
	 * @property {gc.gis.util.GCTreeNode} context
	 * @property {gc.gis.util.GCTreeNode} oldValue
	 * @property {gc.gis.util.GCTreeNode} newValue
	 */
	PARENT_CHANGED:   'change:parent',
	/**
	 * @event gc.gis.util.GCTreeNodeEvent#change:children
	 * @type {Object}
	 * @property {gc.gis.util.GCTreeNode} context
	 * @property {gc.gis.util.GCTreeNode[]} children
	 */
	CHILDREN_CHANGED: 'change:children',
	/**
	 * @event gc.gis.util.GCTreeNodeEvent#add:children
	 * @type {Object}
	 * @property {gc.gis.util.GCTreeNode} context
	 * @property {gc.gis.util.GCTreeNode[]} children
	 */
	CHILDREN_ADDED:   'add:children',
	/**
	 * @event gc.gis.util.GCTreeNodeEvent#remove:children
	 * @type {Object}
	 * @property {gc.gis.util.GCTreeNode} context
	 * @property {gc.gis.util.GCTreeNode[]} children
	 */
	CHILDREN_REMOVED: 'remove:children',
	/**
	 * @event gc.gis.util.GCTreeNodeEvent#add:child
	 * @type {Object}
	 * @property {gc.gis.util.GCTreeNode} context
	 * @property {gc.gis.util.GCTreeNode} child
	 */
	CHILD_ADDED:      'add:child',
	/**
	 * @event gc.gis.util.GCTreeNodeEvent#remove:child
	 * @type {Object}
	 * @property {gc.gis.util.GCTreeNode} context
	 * @property {gc.gis.util.GCTreeNode} child
	 */
	CHILD_REMOVED:    'remove:child'
};

/**
 * @classdesc
 * Baum(knoten)-Implementierung
 *
 * @class
 * @extends gc.gis.GCObservable
 * @memberof gc.gis
 * @author Anton Popov <a.popov@brain-scc.de>
 *
 * @template T
 *
 * @param {!Object} options  Options
 * @param {?T} [options.payload=null] verknüpfte Daten
 * @param {gc.gis.util.GCTreeNode[]} [options.children=[]] Liste von Kindknoten
 * @param {gc.gis.util.GCTreeNode} [options.parentNode=null] Elternknoten
 * @param {boolean} [options.mutable=false] Mutierbarkeit
 */
gc.gis.util.GCTreeNode = function( options )
{
	gc.gis.GCObservable.call( this );

	/**
	 * @protected
	 * @type {T|null}
     */
	this._payload        = options.payload || null;
	/**
	 * @protected
	 * @type {gc.gis.util.GCTreeNode[]}
     */
	this._children       = options.children || [];
	/**
	 * @protected
	 * @type {gc.gis.util.GCTreeNode|null}
     */
	this._parent         = options.parentNode || null;
	/**
	 * @protected
	 * @type {boolean}
	 */
	this._is_mutable     = gc.gis.GCUtil.parseBool( options.mutable, false );

	this._initEvents();
};
gc.gis.util.GCTreeNode.prototype = Object.create( gc.gis.GCObservable.prototype );

/**
 * Event-Bubbling aktivieren
 *
 * @protected
 * @method
 */
gc.gis.util.GCTreeNode.prototype._initEvents = function()
{
	var self = this;
	var bubble = function( evt_type, evt ) {
		if( self.getParent() )
		{
			self.getParent()._fireEvent( evt_type, evt );
		}
	};
	this.on( gc.gis.util.GCTreeNodeEvent.CHILDREN_CHANGED, this, function(evt ) {
		bubble( gc.gis.util.GCTreeNodeEvent.CHILDREN_CHANGED, evt );
	});
	this.on( gc.gis.util.GCTreeNodeEvent.CHILDREN_ADDED, this, function(evt ) {
		bubble( gc.gis.util.GCTreeNodeEvent.CHILDREN_ADDED, evt );
	});
	this.on( gc.gis.util.GCTreeNodeEvent.CHILDREN_REMOVED, this, function(evt ) {
		bubble( gc.gis.util.GCTreeNodeEvent.CHILDREN_REMOVED, evt );
	});
	this.on( gc.gis.util.GCTreeNodeEvent.CHILD_ADDED, this, function(evt ) {
		bubble( gc.gis.util.GCTreeNodeEvent.CHILD_ADDED, evt );
	});
	this.on( gc.gis.util.GCTreeNodeEvent.CHILD_REMOVED, this, function(evt ) {
		bubble( gc.gis.util.GCTreeNodeEvent.CHILD_REMOVED, evt );
	});
};

/**
 * Hat dieser Knoten keinen Elternknoten?
 *
 * @public
 * @method
 * @return {boolean}
 */
gc.gis.util.GCTreeNode.prototype.isRoot = function()
{
	return this.getParent() ? false : true;
};

/**
 * Ist dieser Knoten kinderlos?
 *
 * @public
 * @method
 * @return {boolean}
 */
gc.gis.util.GCTreeNode.prototype.isLeaf = function()
{
	return this._children.length === 0;
};

/**
 * Ist dieser Knoten mutierbar? Bei mutierbaren Knoten können Kindknoten
 * hinzugefügt bzw. entfernt werden und die mit diesem Knoten verknüpften
 * Daten im Nachhinein verändert werden.
 *
 * @public
 * @method
 * @return {boolean}
 */
gc.gis.util.GCTreeNode.prototype.isMutable = function()
{
	return this._is_mutable;
};

/**
 * Gibt die mit diesem Knoten verknüpften Daten zurück.
 *
 * @public
 * @method
 * @return {T} mit diesem Knoten verknüpfte Daten
 */
gc.gis.util.GCTreeNode.prototype.getPayload = function()
{
	return this._payload;
};

/**
 * Gibt eine Liste der mit den Kindknoten dieses Knotens verknüpften Daten
 * zurück.
 *
 * @public
 * @method
 * @param {function} [opt_filter_func]  Optionale Funktion zum Filtern der Ergebnisse
 * @return {T[]} Liste der mit den Kinderknoten verknüpften Daten
 * @see {@link gc.gis.util.GCTreeNode.getPayload}
 */
gc.gis.util.GCTreeNode.prototype.getChildrenPayload = function( opt_filter_func )
{
	var children = this.getChildren();
	var payload = [];

	var do_filter = (typeof opt_filter_func === 'function');

	for( var i=0; i<children.length; i++ )
	{
		var p = children[i].getPayload();

		if( do_filter )
		{
			if( opt_filter_func.call( this, p ) )
			{
				payload.push( p );
			}
		}
		else
		{
			payload.push( p );
		}
	}

	return payload;
};

/**
 * Verknüpft Daten jeglicher Art mit diesem Knoten.
 *
 * @public
 * @method
 * @param {T} payload Daten jeglicher Art
 * @param {boolean} [opt_fire_event=true]  Flag, ob Event abgefeuert werden soll
 * @throws {gc.gis.error.GCError} Diese Instanz muss mutierbar sein!
 * @fires gc.gis.util.GCTreeNodeEvent#change:payload
 */
gc.gis.util.GCTreeNode.prototype.setPayload = function( payload, opt_fire_event )
{
	if( !this.isMutable() )
	{
		throw new gc.gis.error.GCError('Cannot set payload for immutable node');
	}

	var old_payload = this.getPayload();
	this._payload = payload;

	if( opt_fire_event !== false )
	{
		this._fireEvent( gc.gis.util.GCTreeNodeEvent.PAYLOAD_CHANGED, {
			context: this,
			oldValue: old_payload,
			newValue: payload
		});
	}
};

/**
 * Gibt den Elternknoten dieses Knotens zurück.
 *
 * @public
 * @method
 * @return {gc.gis.util.GCTreeNode} Elternknoten dieses Knotens
 */
gc.gis.util.GCTreeNode.prototype.getParent = function()
{
	return this._parent;
};

/**
 * Setzt den Elternknoten dieses Knotens.
 *
 * @public
 * @method
 * @param {gc.gis.util.GCTreeNode} parent_node neuer Elternknoten
 * @param {boolean} [opt_fire_event=true]  Flag, ob Event abgefeuert werden soll
 * @fires gc.gis.util.GCTreeNodeEvent#change:payload
 */
gc.gis.util.GCTreeNode.prototype.setParent = function( parent_node, opt_fire_event )
{
	var old_parent = this.getParent();
	this._parent = parent_node;

	if( opt_fire_event !== false )
	{
		this._fireEvent( gc.gis.util.GCTreeNodeEvent.PARENT_CHANGED, {
			context: this,
			oldValue: old_parent,
			newValue: parent_node
		});
	}
};

/**
 * Gibt die Liste der Kindknoten dieses Knotens zurück.
 *
 * @public
 * @method
 * @return {gc.gis.util.GCTreeNode[]} Liste von Kindknoten dieses Knotens
 */
gc.gis.util.GCTreeNode.prototype.getChildren = function()
{
	return this._children;
};

/**
 * Gibt eine Liste aller Kindknoten (d.h. rekursiv nach unten) dieses Knotens zurück.
 *
 * @public
 * @method
 * @return {gc.gis.util.GCTreeNode[]} Liste von Kindknoten dieses Knotens (rekursiv)
 */
gc.gis.util.GCTreeNode.prototype.getChildrenRecursive = function()
{
	var childs = [], i, ii;

	for(i = 0, ii = this._children.length; i < ii; i++)
	{
		childs.push(this._children[i]);
		if (!this._children[i].isLeaf())
		{
			childs = childs.concat(this._children[i].getChildrenRecursive());
		}
	}

	return childs;
};

/**
 * Gibt den Knoten am gegebenen Index zurück.
 *
 * @public
 * @method
 * @param {number} index  Index (Integer)
 * @return {gc.gis.util.GCTreeNode} Kindknoten am gegebenen Index
 * @throws {gc.gis.error.GCError} Index muss gültig sein
 */
gc.gis.util.GCTreeNode.prototype.getChildAt = function( index )
{
	if( index >= this._children.length )
	{
		throw new gc.gis.error.GCError('Invalid index: '+ index +' (size: '+ this._children.length +')');
	}

	return this._children[index];
};

/**
 * Gibt den Index eines Kindknotens zurück.
 *
 * @public
 * @method
 * @param {gc.gis.util.GCTreeNode} child_node  Kindknoten
 * @return {number} Index des Knotens oder <code>-1</code>, wenn der Knoten kein Kind
 *   dieses Knotens ist
 */
gc.gis.util.GCTreeNode.prototype.indexOf = function( child_node )
{
	return this._children.indexOf( child_node );
};

/**
 * Geht rekursiv den eigenen Baum nach oben durch und gibt den Indexpfad vom
 * Wurzelknoten bis zu diesem Knoten aus.
 *
 * @public
 * @method
 * @return {number[]} Indexpfad
 */
gc.gis.util.GCTreeNode.prototype.getPath = function()
{
	var path = [];
	if( this.getParent() && this.getParent().getPath() )
	{
		path = this.getParent().getPath().concat( this.getParent().indexOf( this ) );
	}
	return path;
};

/**
 * Sucht einen Knoten rekursiv in diesem Teilbaum.
 *
 * @public
 * @method
 * @param {number[]} path  Indexpfad zum gesuchten Knoten
 * @return {gc.gis.util.GCTreeNode|null} gesuchter Knoten oder <code>null</code>, wenn nicht
 *   gefunden
 */
gc.gis.util.GCTreeNode.prototype.findNode = function( path )
{
	if( !this.isLeaf() && path.length > 0 )
	{
		if( path[0] < this.getChildren().length )
		{
			var child = this.getChildAt( path[0] );
			if( path.length === 1 )
			{
				return child;
			}
			else
			{
				path.splice( 0, 1 );
				return child.findNode( path );
			}
		}
	}
	return null;
};

/**
 * Ersetzt alle alten Kindknoten durch neue. Entfernt die Knoten vor dem
 * Einfügen von ihren Elternknoten.
 *
 * @public
 * @method
 * @param {gc.gis.util.GCTreeNode[]} children einzufügende Knoten
 * @param {boolean} [opt_fire_event=true]  Flag, ob Event abgefeuert werden soll
 * @throws {gc.gis.error.GCError} Diese Instanz muss mutierbar sein!
 * @fires gc.gis.util.GCTreeNodeEvent#change:children
 * @see {@link gc.gis.util.GCTreeNode.removeAll}
 * @see {@link gc.gis.util.GCTreeNode.addChildren}
 */
gc.gis.util.GCTreeNode.prototype.setChildren = function( children, opt_fire_event )
{
	this.removeAll( false );
	this.addChildren( children, false );

	if( opt_fire_event !== false )
	{
		this._fireEvent( gc.gis.util.GCTreeNodeEvent.CHILDREN_CHANGED, {
			context: this,
			children: children
		});
	}
};

/**
 * Fügt mehrere Knoten ein. Entfernt die Knoten vor dem Einfügen von ihren
 * Elternknoten.
 *
 * @public
 * @method
 * @param {gc.gis.util.GCTreeNode[]} children einzufügende Knoten
 * @param {boolean} [opt_fire_event=true]  Flag, ob Event abgefeuert werden soll
 * @throws {gc.gis.error.GCError} Diese Instanz muss mutierbar sein!
 * @fires gc.gis.util.GCTreeNodeEvent#add:children
 * @see {@link gc.gis.util.GCTreeNode.insertAt}
 */
gc.gis.util.GCTreeNode.prototype.addChildren = function( children, opt_fire_event )
{
	for( var i=0; i<children.length; i++ )
	{
		this.insertAt( children[i], this._children.length, false );
	}

	if( opt_fire_event !== false )
	{
		this._fireEvent( gc.gis.util.GCTreeNodeEvent.CHILDREN_ADDED, {
			context: this,
			children: children
		});
	}
};

/**
 * Fügt einen Knoten ein. Entfernt den Knoten vor dem Einfügen von seinem
 * Elternknoten.
 *
 * @public
 * @method
 * @param {gc.gis.util.GCTreeNode} child_node einzufügender Knoten
 * @param {boolean} [opt_fire_event=true]  Flag, ob Event abgefeuert werden soll
 * @throws {gc.gis.error.GCError} Diese Instanz muss mutierbar sein!
 * @fires gc.gis.util.GCTreeNodeEvent#add:child
 * @see {@link gc.gis.util.GCTreeNode.insertAt}
 */
gc.gis.util.GCTreeNode.prototype.addChild = function( child_node, opt_fire_event )
{
	this.insertAt( child_node, this._children.length, opt_fire_event );
};

/**
 * Fügt einen Knoten an einer bestimmten Stelle ein. Ist der Index größer als
 * die Anzahl der bereits existierenden Kinder, wird der Knoten am Ende
 * eingefügt. Entfernt den Knoten vor dem Einfügen von seinem Elternknoten.
 *
 * @public
 * @method
 * @param {gc.gis.util.GCTreeNode} child_node einzufügender Knoten
 * @param {number} index Index des zu einzufügenden Knotens (Integer)
 * @param {boolean} [opt_fire_event=true]  Flag, ob Event abgefeuert werden soll
 * @fires gc.gis.util.GCTreeNodeEvent#add:child
 * @throws {gc.gis.error.GCError} Diese Instanz muss mutierbar sein!
 */
gc.gis.util.GCTreeNode.prototype.insertAt = function( child_node, index, opt_fire_event )
{
	if( !this.isMutable() )
	{
		throw new gc.gis.error.GCError('Cannot add child to immutable node');
	}

	// Ggf. Index anpassen
	if( index > this._children.length )
	{
		index = this._children.length;
	}

	// Ggf. von Parent ablösen
	if( child_node.getParent() !== null )
	{
		child_node.getParent().removeChild( child_node, false );
	}

	// Parent neu setzen
	child_node.setParent( this, false );

	// hinzufügen
	this._children.splice( index, 0, child_node );

	// Ggf. Event abfeuern
	if( opt_fire_event !== false )
	{
		this._fireEvent( gc.gis.util.GCTreeNodeEvent.CHILD_ADDED, {
			context: this,
			child: child_node
		});
	}
};

/**
 * Entfernt einen Kindknoten.
 *
 * @public
 * @method
 * @param {gc.gis.util.GCTreeNode} child_node zu entfernender Knoten
 * @param {boolean} [opt_fire_event=true]  Flag, ob Event abgefeuert werden soll
 * @throws {gc.gis.error.GCError} Diese Instanz muss mutierbar sein! Der
 *   übergebene Konten muss ein Kind dieses Knotens sein
 * @fires gc.gis.util.GCTreeNodeEvent#remove:child
 * @see {@link gc.gis.util.GCTreeNode.removeChildAt}
 */
gc.gis.util.GCTreeNode.prototype.removeChild = function( child_node, opt_fire_event )
{
	var index = this.indexOf( child_node );
	if( index < 0 )
	{
		throw new gc.gis.error.GCError('Given node is not a child of this node');
	}

	this.removeChildAt( index, opt_fire_event );
};

/**
 * Entfernt einen Kindknoten anhand seines Indexes. Setzt seinen Elternknoten
 * (diese Instanz) auf <code>null</code>.
 *
 * @public
 * @method
 * @param {number} index Index des zu entfernenden Knotens (Integer)
 * @param {boolean} [opt_fire_event=true]  Flag, ob Event abgefeuert werden soll
 * @throws {gc.gis.error.GCError} Diese Instanz muss mutierbar sein!
 * @fires gc.gis.util.GCTreeNodeEvent#remove:child
 */
gc.gis.util.GCTreeNode.prototype.removeChildAt = function(index, opt_fire_event)
{
	if( !this.isMutable() )
	{
		throw new gc.gis.error.GCError('Cannot remove child from immutable node');
	}

	var child = this.getChildAt(index),
		childs_removed = [];

	childs_removed.push(child);
	childs_removed = childs_removed.concat(child.getChildrenRecursive());
	child.setParent(null, false);

	this._children.splice(index, 1);

	if(opt_fire_event !== false)
	{
		this._fireEvent(gc.gis.util.GCTreeNodeEvent.CHILD_REMOVED, {
			context: this,
			child: child,               // der betroffene Child
			children: childs_removed    // alle Childs rekursiv, die gelöscht wurden
		});
	}

	return child;
};

/**
 * Alle Kindknoten entfernen.
 *
 * @public
 * @method
 * @param {boolean} [opt_fire_event=true]  Flag, ob Event abgefeuert werden soll
 * @throws {gc.gis.error.GCError} Diese Instanz muss mutierbar sein!
 * @fires gc.gis.util.GCTreeNodeEvent#remove:children
 */
gc.gis.util.GCTreeNode.prototype.removeAll = function( opt_fire_event )
{
	if(!this.isMutable())
	{
		throw new gc.gis.error.GCError('Cannot remove from immutable node');
	}

	// Children als Clone merken (Clone wird in getChildrenRecursive erzeugt), da diese nach dem Löschen im Event benötigt werden
	// alle Children rekursiv holen, da diese ja auch gelöscht werden
	var children = this.getChildrenRecursive();

	while (this._children.length > 0)
	{
		this.removeChild(this._children[0], false);
	}

	if(opt_fire_event !== false)
	{
		this._fireEvent(gc.gis.util.GCTreeNodeEvent.CHILDREN_REMOVED, {
			context: this,
			children: children
		});
	}
};